import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEvent } from "@/context/EventContext";
import { Printer, ArrowLeft, Heart } from "lucide-react";

const PRINT_W = 720;

export default function PrintSeating() {
  const navigate = useNavigate();
  const { eapi, event, eventId } = useEvent();
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [bpUrl, setBpUrl] = useState(null);
  const [canvas, setCanvas] = useState(null);
  const title = event?.title || "Seating Chart";

  useEffect(() => {
    Promise.all([eapi.get("/tables"), eapi.get("/guests")]).then(([t, g]) => { setTables(t.data); setGuests(g.data); }).catch(() => {});
    (async () => {
      try {
        const m = await eapi.get("/blueprint");
        if (m.data.blueprint) {
          const b = await eapi.get("/blueprint/file", { responseType: "blob" });
          setBpUrl(URL.createObjectURL(b.data));
          setCanvas(m.data.canvas || null);
        }
      } catch { /* none */ }
    })();
  }, [eapi]);

  const attending = guests.filter((g) => g.rsvp_status === "attending");
  const seatedOf = (tid) => attending.filter((g) => g.table_id === tid);
  const placeCards = attending.filter((g) => g.table_id).map((g) => ({ name: `${g.first_name} ${g.last_name}`, table: tables.find((t) => t.id === g.table_id)?.name || "" }));

  const scale = canvas && canvas.w ? PRINT_W / canvas.w : null;

  return (
    <div style={{ background: "#fff" }} className="min-h-screen text-stone-800">
      <div className="print:hidden sticky top-0 bg-white border-b border-[#E8E6E1] px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate(`/e/${eventId}/seating`)} data-testid="print-back-btn" className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800"><ArrowLeft size={16} /> Back to seating</button>
        <button onClick={() => window.print()} data-testid="do-print-btn" className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-5 py-2 text-sm hover:bg-[#8C9A86] transition-colors"><Printer size={15} /> Print</button>
      </div>
      <div className="max-w-4xl mx-auto px-8 py-10">
        <div className="text-center mb-10"><Heart size={20} className="mx-auto text-[#8C9A86] mb-2" fill="#8C9A86" /><h1 className="font-serif text-4xl tracking-tight">{title}</h1><p className="text-sm text-stone-400 mt-1">Seating Chart</p></div>

        {bpUrl && (
          <div className="mb-10 break-inside-avoid flex flex-col items-center" data-testid="print-floorplan">
            <h2 className="font-serif text-2xl text-center mb-4">Venue Floor Plan</h2>
            {scale ? (
              <div className="relative border border-stone-300 rounded-lg overflow-hidden bg-[#FBFAF6]"
                style={{ width: PRINT_W, height: Math.max(200, canvas.h * scale) }}>
                <img src={bpUrl} alt="Floor plan" className="absolute inset-0 w-full h-full object-contain" />
                {tables.map((t) => {
                  const round = t.shape === "round";
                  return (
                    <div key={t.id} data-testid={`print-table-${t.id}`}
                      style={{ left: (t.x || 0) * scale, top: (t.y || 0) * scale, width: (t.w || 150) * scale, height: (t.h || 150) * scale }}
                      className={`absolute flex flex-col items-center justify-center bg-white/90 border-2 border-stone-400 ${round ? "" : "rounded-md"}`}>
                      <span className="text-[10px] font-medium text-stone-700 leading-tight px-1 text-center truncate max-w-full" style={round ? { borderRadius: "50%" } : {}}>{t.name}</span>
                      <span className="text-xs text-stone-500">{seatedOf(t.id).length}/{t.capacity}</span>
                      {round && <span className="absolute inset-0 border-2 border-stone-400 rounded-full pointer-events-none" />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <img src={bpUrl} alt="Floor plan" className="w-full object-contain border border-stone-300 rounded-lg" style={{ maxHeight: 520 }} />
            )}
            <p className="text-xs text-stone-400 mt-2">Table positions as arranged in your planner.</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-6">
          {tables.map((t) => (
            <div key={t.id} className="border border-stone-300 rounded-lg p-5 break-inside-avoid">
              <p className="font-serif text-2xl border-b border-stone-200 pb-2 mb-3">{t.name}</p>
              <ol className="space-y-1 text-sm">{seatedOf(t.id).map((g) => <li key={g.id}>{g.first_name} {g.last_name}</li>)}{seatedOf(t.id).length === 0 && <li className="text-stone-300 italic">No guests seated</li>}</ol>
            </div>))}
        </div>
        <div className="mt-12" style={{ pageBreakBefore: "always" }}>
          <h2 className="font-serif text-3xl text-center mb-6">Place Cards</h2>
          <div className="grid grid-cols-2 gap-4">
            {placeCards.map((p, i) => (<div key={i} className="border border-stone-300 rounded-lg p-6 text-center break-inside-avoid"><p className="overline">{title}</p><p className="font-serif text-2xl mt-2">{p.name}</p><p className="text-sm text-[#8C9A86] mt-1">{p.table}</p></div>))}
            {placeCards.length === 0 && <p className="col-span-2 text-center text-sm text-stone-400">Seat guests to generate place cards.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
